import { useState } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import useI18n from "../../hooks/useTranslations";
import SvgTextSnippet from "../icons/TextSnippet";
import styles from "./TranslatableField.module.css";
import Icon from "./Icon";
import TranslationsModalDialog, { TranslationDialogFieldName } from "./TranslationsModalDialog";

type TranslatableFieldProps<
  TForm extends FieldValues,
  TField extends TranslationDialogFieldName<TForm>,
> = {
  field: TField;
  formValues: TForm;
  setFormValue: UseFormSetValue<TForm>;
  dialogTitle?: string;
  children: React.ReactNode;
} & (
  | {
      dialogType?: "input";
      dialogRows?: never;
    }
  | {
      dialogType: "textarea";
      dialogRows?: number;
    }
);

const TranslatableField = <TForm extends FieldValues, TField extends TranslationDialogFieldName<TForm>>({
  field,
  formValues,
  setFormValue,
  dialogTitle,
  children,
  dialogType = "input",
  dialogRows = 5,
}: TranslatableFieldProps<TForm, TField>) => {
  const i18n = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className={styles.translations}>
        {children}
        <div className={styles.wrapper}>
          <button
            type="button"
            className={styles.button}
            onClick={() => setIsOpen(true)}
            title={i18n.translation.common.translations}
          >
            <Icon glyph={SvgTextSnippet} className={styles.icon} />
          </button>
        </div>
      </div>
      <TranslationsModalDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        field={field}
        values={formValues}
        setValue={setFormValue}
        title={dialogTitle}
        {...(dialogType === "textarea"
          ? { type: dialogType, rows: dialogRows }
          : { type: dialogType })}
      />
    </>
  );
};

export default TranslatableField;
